import { z } from 'zod';
import { defineContract } from './contract';

/**
 * Garrabha's public open-trials API (`/v1/public/trials`, product `garrabha`). STRICT: exactly the
 * fields of `products/garrabha/studio/site-content.md` section 4. A field the server adds by
 * mistake (an id, the tenant, the number of offers, anything about a trier) fails the contract test.
 *
 * Only trials that are approved, not past their deadline and with a place left are ever returned.
 * `slotsLeft` is all the page learns about places: the total and the number taken stay private.
 */
export const publicTrialSchema = z.strictObject({
  code: z.string().regex(/^[A-Za-z0-9]{7}$/),
  title: z.string(),
  categoryName: z.string().nullable(),
  regionName: z.string().nullable(),
  cityName: z.string().nullable(),
  details: z.string(),
  isFree: z.boolean(),
  amountEgp: z.number().int().positive().nullable(),
  slotsLeft: z.number().int().min(1),
  platforms: z.array(z.string()),
  /** A Cairo calendar day, `YYYY-MM-DD`. */
  deadline: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  approvedAt: z.iso.datetime(),
  ownerDisplayName: z.string().nullable(),
});
export type PublicTrial = z.infer<typeof publicTrialSchema>;

export const PUBLIC_TRIALS_MAX_PAGE_SIZE = 50;

export const publicTrialsPageSchema = z.strictObject({
  data: z.array(publicTrialSchema),
  meta: z.strictObject({
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
    totalPages: z.number().int().min(0),
  }),
});
export type PublicTrialsPage = z.infer<typeof publicTrialsPageSchema>;

export const listPublicTrialsQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(PUBLIC_TRIALS_MAX_PAGE_SIZE).default(20),
});
export type ListPublicTrialsQuery = z.input<typeof listPublicTrialsQuery>;

export const publicTrialsContracts = {
  list: defineContract({
    method: 'GET',
    path: '/public/trials',
    query: listPublicTrialsQuery,
    response: publicTrialsPageSchema,
  }),
  get: defineContract({
    method: 'GET',
    path: '/public/trials/:code',
    response: publicTrialSchema,
  }),
};
